import { Controller } from "@hotwired/stimulus"
import helpers from "../globals/helpers"
import constants from "../globals/constants.js"

const COLOR_ARROW_PRODUCER = constants.COLOR_GREY_DARKER
const COLOR_ARROW_CONSUMER = constants.COLOR_GREY_DARKER

export default class EnergyFlowGraphController extends Controller {
  static values = {
    siteId: String,
    refreshInterval: { type: Number, default: 12_000 },
    autoRefresh: { type: Boolean, default: true },
    debug: { type: Boolean, default: false }
  }

  static targets = [
    "producer", "storage",
    "centerCircle", "producerCircle", "consumerCircle", "gridCircle", "storageCircle",
    "heatingCircle", "chargingCircle", "generalConsumptionCircle",
    "producerValueText", "consumerValueText", "gridValue", "gridValueText", "storageValueText",
    "heatingValueText", "chargingValueText", "generalConsumptionValueText",
    "centerHeading", "centerValue", "autarkyValue", "emsArc",
    "storageStateOfChargeOverlay", "storageStateOfChargeText",
    "producerConnection", "consumerConnection", "gridConnection", "storageConnection",
    "heatingConnection", "chargingConnection", "generalConsumptionConnection",
    "producerFootnote", "consumerFootnote", "gridFootnote", "storageFootnote",
    "producersOfflineNote", "gridOfflineNote", "consumptionInaccurateNote", "storagesOfflineNote",
    "gridFeedInPrioritizationNote", "gridFeedInPrioritizationNoteText",
    "gridFeedInPrioritizationFootnote",
  ]

  connect() {
    this.debug && console.log('[EnergyFlowGraph] Controller connected', { siteId: this.siteIdValue })
    this.initializeValues()
    if (this.autoRefreshValue) {
      this.startPolling()
    }
    this.addTurboEventListeners()
  }

  disconnect() {
    this.debug && console.log('[EnergyFlowGraph] Controller disconnected')
    this.removeTurboEventListeners()
    this.stopPolling()
  }

  addTurboEventListeners() {
    this.turboBeforeVisitHandler = this.handleTurboBeforeVisit.bind(this)
    this.turboBeforeRenderHandler = this.handleTurboBeforeRender.bind(this)

    document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    document.addEventListener('turbo:before-render', this.turboBeforeRenderHandler)
  }

  removeTurboEventListeners() {
    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
    }
    if (this.turboBeforeRenderHandler) {
      document.removeEventListener('turbo:before-render', this.turboBeforeRenderHandler)
    }
  }

  handleTurboBeforeVisit() {
    this.debug && console.log('[EnergyFlowGraph] Turbo before visit')
    this.stopPolling()
  }

  handleTurboBeforeRender(event) {
    this.debug && console.log('[EnergyFlowGraph] Turbo before render')
    if (event.detail?.newStream) {
      this.clearAnimations()
    }
  }

  initializeValues() {
    this.flowValues = {
      producer: "-",
      totalConsumption: "-",
      grid: "-",
      storage: "-",
      charging: "-",
      heating: "-",
      generalConsumption: "-",
      stateOfCharge: null,
    }

    this.autarky = "-"
    this.connectionBasis = null
    this.consumptionInaccurate = false
    this.gridFeedInPrioritizationNotice = null
    this.pollingTimer = null
    this.lastDataHash = null
  }

  startPolling() {
    this.poll()
    this.pollingTimer = setInterval(() => this.poll(), this.refreshIntervalValue)
    this.debug && console.log('[EnergyFlowGraph] Polling started', { interval: this.refreshIntervalValue })
  }

  async poll() {
    if (this.isPolling) {
      this.debug && console.log('[EnergyFlowGraph] Skipping poll - already in progress')
      return
    }

    if (document.hidden) {
      return
    }

    this.isPolling = true
    this.setLoadingState(true)

    try {
      await this.requestData()
    } catch (error) {
      this.handlePollingError(error)
    } finally {
      this.isPolling = false
      this.setLoadingState(false)
    }
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
      this.debug && console.log('[EnergyFlowGraph] Polling stopped')
    }
  }

  setLoadingState(isLoading) {
    this.debug && console.log('[EnergyFlowGraph] Loading state', { isLoading })
  }

  handlePollingError(error) {
    console.error('[EnergyFlowGraph] Polling error:', error)

    this.errorCount = (this.errorCount || 0) + 1
    const backoffDelay = Math.min(1000 * Math.pow(2, this.errorCount), 30000)

    this.debug && console.log('[EnergyFlowGraph] Error recovery', { errorCount: this.errorCount, backoffDelay })

    setTimeout(() => {
      this.errorCount = Math.max(0, this.errorCount - 1)
    }, backoffDelay)
  }

  async requestData() {
    const url = `/api/v1/sites/${this.siteIdValue}/ems/current`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
    }

    // Create compatible AbortController with timeout for older browsers
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      const parsedData = this.parseData(data)
      this.updateChart(parsedData)
      this.errorCount = 0
    } catch (error) {
      clearTimeout(timeoutId)

      if (error.name === 'AbortError') {
        console.warn('[EnergyFlowGraph] Request timeout')
      } else {
        console.error('[EnergyFlowGraph] API request failed:', error)
      }
      throw error
    }
  }

  parseData(data) {
    return {
      gateways_online_state: data.gateways_online_state || 'offline',
      power_grid: data.power_grid?.value || 0,
      power_storage: data.power_storage?.value || 0,
      power_production: data.power_production?.value || 0,
      power_consumption: data.power_consumption?.value || 0,
      power_consumption_calc: data.power_consumption_calc?.value || 0,
      power_charging_stations: data.power_charging_stations?.value || 0,
      power_heating: data.power_heating?.value || 0,
      power_appliances: data.power_appliances?.value || 0,
      self_sufficiency: data.self_sufficiency?.value || 0,
      state_of_charge: data.state_of_charge?.value ?? null,
      producers_total_count: data.producers_total_count || 0,
      producers_online_count: data.producers_online_count || 0,
      storages_total_count: data.storages_total_count || 0,
      storages_online_count: data.storages_online_count || 0,
      grids_total_count: data.grids_total_count || 0,
      grids_online_count: data.grids_online_count || 0,
      charging_points_total_count: data.charging_points_total_count || 0,
      charging_points_online_count: data.charging_points_online_count || 0,
      heatings_total_count: data.heatings_total_count || 0,
      heatings_online_count: data.heatings_online_count || 0,
      grid_feed_in_prioritization_notice: data.grid_feed_in_prioritization_notice
    }
  }

  updateChart(data) {
    try {
      const newDataHash = JSON.stringify(data)
      if (this.lastDataHash === newDataHash) {
        return
      }
      this.lastDataHash = newDataHash

      this.clearAnimations()
      this.updateState(data)
      this.updateFootnotes()
      this.updateCircles(data)
      this.setAutarky(data.self_sufficiency)
      this.updateConnections(data)
    } catch (error) {
      console.error('[EnergyFlowGraph] Data processing error:', error)
      this.debug && console.error('[EnergyFlowGraph] Error details:', {
        error: error.message,
        stack: error.stack,
        data: data,
        targets: {
          hasHeatingCircle: this.hasHeatingCircleTarget,
          hasChargingCircle: this.hasChargingCircleTarget,
          hasProducerCircle: this.hasProducerCircleTarget,
          hasStorageCircle: this.hasStorageCircleTarget,
          hasGridCircle: this.hasGridCircleTarget,
          hasConsumerCircle: this.hasConsumerCircleTarget
        }
      })
    }
  }

  updateState(data) {
    this.gatewayOffline = data.gateways_online_state !== 'online'
    this.gridFeedInPrioritizationNotice = data.grid_feed_in_prioritization_notice
    this.updateGridProblemState(data)
    this.offlineStates = {
      producers: data.producers_total_count > 0 ? {
        someOffline: data.producers_total_count > data.producers_online_count,
        allOffline: data.producers_online_count === 0,
        totalCount: data.producers_total_count
      } : null,
      storages: data.storages_total_count > 0 ? {
        someOffline: data.storages_total_count > data.storages_online_count,
        allOffline: data.storages_online_count === 0,
        totalCount: data.storages_total_count
      } : null,
      grid: data.grids_total_count > 0 ? {
        someOffline: data.grids_total_count > data.grids_online_count,
        allOffline: data.grids_online_count === 0,
        totalCount: data.grids_total_count
      } : null,
      charging: data.charging_points_total_count > 0 ? {
        someOffline: data.charging_points_total_count > data.charging_points_online_count,
        allOffline: data.charging_points_online_count === 0,
        totalCount: data.charging_points_total_count
      } : null,
      heating: data.heatings_total_count > 0 ? {
        someOffline: data.heatings_total_count > data.heatings_online_count,
        allOffline: data.heatings_online_count === 0,
        totalCount: data.heatings_total_count
      } : null
    }
    this.specialCases = {
      gridProducerBatteryAffected: !!(this.offlineStates.producers?.someOffline ||
                                      this.offlineStates.storages?.someOffline ||
                                      this.offlineStates.grid?.someOffline),

      consumersAffected: !!(this.offlineStates.charging?.someOffline ||
                           this.offlineStates.heating?.someOffline)
    }
    this.consumptionInaccurate = !!(data.grids_total_count === 0 ||
                                    this.offlineStates.producers?.someOffline ||
                                    this.offlineStates.grid?.someOffline ||
                                    this.offlineStates.storages?.someOffline)
  }

  updateGridProblemState(data) {
    if (!this.hasGridCircleTarget || !this.hasGridValueTarget) return

    const method = data.grids_total_count === 0 ? 'add' : 'remove'
    this.gridCircleTarget.classList[method]('circle--problem')
    this.gridValueTarget.classList[method]('circle--problem')
  }

  // Helper method to check if a device category has online devices
  hasOnlineDevices(categoryKey) {
    return this.offlineStates[categoryKey] && !this.offlineStates[categoryKey].allOffline
  }

  updateCircles(data) {
    this.processGridData(data)
    this.processProducerData(data)
    this.processStorageData(data)
    this.processConsumerData(data)
    this.processChargingData(data)
    this.processHeatingData(data)
    this.processGeneralConsumption(data)
  }

  processGridData(data) {
    const config = {
      valueKey: 'grid',
      targetKey: 'gridValueTextTarget',
      dataKey: 'power_grid', // Use flattened key
      offlineStateKey: 'grid'
    }
    this.processCircleValue(config, data)
  }

  processProducerData(data) {
    const config = {
      valueKey: 'producer',
      targetKey: 'producerValueTextTarget',
      dataKey: 'power_production', // Use flattened key
      offlineStateKey: 'producers',
      circleTargetKey: 'producerTarget',
    }

    this.processCircleValue(config, data)
  }

  processStorageData(data) {
    const config = {
      valueKey: 'storage',
      targetKey: 'storageValueTextTarget',
      dataKey: 'power_storage', // Use flattened key
      offlineStateKey: 'storages',
      circleTargetKey: 'storageTarget'
    }
    this.processCircleValue(config, data)

    if (this.gatewayOffline) {
      return
    }
    this.processStateOfCharge(data)
  }

  processStateOfCharge(data) {
    const stateOfChargeValue = data.state_of_charge

    if (stateOfChargeValue !== null && stateOfChargeValue !== undefined && this.hasOnlineDevices('storages')) {
      const formattedValue = parseFloat(stateOfChargeValue.toFixed(1))
      this.flowValues.stateOfCharge = formattedValue + ' %'
      if (this.hasStorageStateOfChargeTextTarget) {
        this.storageStateOfChargeTextTarget.textContent = this.flowValues.stateOfCharge
      }
      if (this.hasStorageStateOfChargeOverlayTarget) {
        this.storageStateOfChargeOverlayTarget.classList.remove("hide")
      }
    } else if (this.hasStorageStateOfChargeOverlayTarget) {
      this.storageStateOfChargeOverlayTarget.classList.add("hide")
    }
  }

  processConsumerData(data) {
    if (this.gatewayOffline) {
      this.setCircleValue('totalConsumption', 'consumerValueTextTarget', "-")
      return
    }
    if (this.specialCases.gridProducerBatteryAffected) {
      this.setCircleValue('totalConsumption', 'consumerValueTextTarget', "-")
      return
    }

    const energyConsumed = data.power_consumption_calc || data.power_consumption
    if (energyConsumed && energyConsumed < 0) {
      this.setCircleValue('totalConsumption', 'consumerValueTextTarget', "-")
    } else if (energyConsumed && energyConsumed > 0) {
      const optimizedValue = this.uiOptimizedValue(energyConsumed)
      this.setCircleValue('totalConsumption', 'consumerValueTextTarget', optimizedValue)
    } else {
      this.setCircleValue('totalConsumption', 'consumerValueTextTarget', "-")
    }
  }

  processChargingData(data) {
    const config = {
      hasTarget: 'hasChargingValueTextTarget',
      valueKey: 'charging',
      targetKey: 'chargingValueTextTarget',
      offlineStateKey: 'charging',
      dataKey: 'power_charging_stations', // Use flattened key
    }
    this.processSmallCircleValue(config, data)
  }

  processHeatingData(data) {
    const config = {
      hasTarget: 'hasHeatingValueTextTarget',
      valueKey: 'heating',
      targetKey: 'heatingValueTextTarget',
      offlineStateKey: 'heating',
      dataKey: 'power_heating', // Use flattened key
    }
    this.processSmallCircleValue(config, data)
  }

  processSmallCircleValue(config, data) {
    if (!this[config.hasTarget]) return

    // Check if the category has any devices (offlineStates exists)
    if (!this.offlineStates[config.offlineStateKey]) {
      return
    }

    if (this.gatewayOffline) {
      this.setCircleValue(config.valueKey, config.targetKey, "-")
      return
    }

    if (this.offlineStates[config.offlineStateKey].allOffline) {
      this.setCircleValue(config.valueKey, config.targetKey, "-")
      return
    }
    const value = data[config.dataKey]

    if (value !== null && value !== undefined) {
      if (value < 0) {
        this.setCircleValue(config.valueKey, config.targetKey, "-")
      } else {
        const optimizedValue = this.uiOptimizedValue(value)
        this.setCircleValue(config.valueKey, config.targetKey, optimizedValue)
      }
    } else {
      this.setCircleValue(config.valueKey, config.targetKey, "-")
    }
  }

  processGeneralConsumption(data) {
    if (!this.hasGeneralConsumptionValueTextTarget) return
    if (this.gatewayOffline) {
      this.setCircleValue('generalConsumption', 'generalConsumptionValueTextTarget', "-")
      return
    }
    if (this.specialCases.gridProducerBatteryAffected) {
      this.setCircleValue('generalConsumption', 'generalConsumptionValueTextTarget', "-")
      return
    }
    if (this.specialCases.consumersAffected) {
      this.setCircleValue('generalConsumption', 'generalConsumptionValueTextTarget', "-")
      return
    }

    const chargingPointOnline = this.hasOnlineDevices('charging')
    const heatingOnline = this.hasOnlineDevices('heating')
    const appliancesValue = data.power_appliances

    if ((chargingPointOnline || heatingOnline) && appliancesValue !== null && appliancesValue !== undefined) {
      if (appliancesValue < 0) {
        this.setCircleValue('generalConsumption', 'generalConsumptionValueTextTarget', "-")
      } else {
        const optimizedValue = this.uiOptimizedValue(appliancesValue)
        this.setCircleValue('generalConsumption', 'generalConsumptionValueTextTarget', optimizedValue)
      }
    } else {
      this.setCircleValue('generalConsumption', 'generalConsumptionValueTextTarget', "-")
    }
  }

  setAutarky(autarky) {
    if (this.gatewayOffline) {
      this.autarky = "-"
      this.updateAutarkyDisplay()
      return
    }

    if (this.specialCases && this.specialCases.gridProducerBatteryAffected) {
      this.autarky = "-"
    } else if (autarky !== null && autarky !== undefined) {
      this.autarky = `${autarky.toFixed(0)} %`
    } else {
      this.autarky = "-"
    }
    this.updateAutarkyDisplay()
  }

  updateAutarkyDisplay() {
    if (this.hasAutarkyValueTarget) {
      this.autarkyValueTarget.textContent = this.autarky
    } else if (this.hasCenterValueTarget) {
      this.centerValueTarget.innerHTML = `<tspan data-energy-flow-graph-target="autarkyValue">${this.autarky} %</tspan>%`
    } else {
      console.warn("[EnergyFlowGraph] Neither autarky value target nor center value target found!")
    }
    this.updateEmsArc()

    if (this.hasCenterCircleTarget) {
      this.centerCircleTarget.classList.remove('hide')
    }
  }

  updateEmsArc() {
    if (!this.hasEmsArcTarget) return

    const autarkyNumeric = this.autarky === "-" ? 0 : parseFloat(this.autarky)
    if (this.autarky === "-" || autarkyNumeric >= 100) {
      this.emsArcTarget.classList.add('hide')
      return
    }

    this.emsArcTarget.classList.remove('hide')
    const radius = 75
    const angle = (autarkyNumeric / 100) * 360
    const startAngle = -90
    const endAngle = startAngle + angle
    const startX = radius * Math.cos(startAngle * Math.PI / 180)
    const startY = radius * Math.sin(startAngle * Math.PI / 180)
    const endX = radius * Math.cos(endAngle * Math.PI / 180)
    const endY = radius * Math.sin(endAngle * Math.PI / 180)
    const largeArcFlag = angle > 180 ? 1 : 0
    const pathData = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`

    this.emsArcTarget.setAttribute('d', pathData)
    this.emsArcTarget.setAttribute('fill', 'none')
    this.emsArcTarget.setAttribute('stroke', '#007DA0')
    this.emsArcTarget.setAttribute('stroke-width', '8')
  }

  updateFootnotes() {
    const gridFeedInPrioritizationVisible = !!this.gridFeedInPrioritizationNotice &&
      !this.gatewayOffline &&
      !this.offlineStates?.grid?.allOffline
    const hasVisibleFootnotes = [
      ['producerFootnote', 'producersOfflineNote', this.offlineStates?.producers?.someOffline],
      ['gridFootnote', 'gridOfflineNote', this.offlineStates?.grid?.someOffline],
      ['consumerFootnote', 'consumptionInaccurateNote', this.consumptionInaccurate],
      ['storageFootnote', 'storagesOfflineNote', this.offlineStates?.storages?.someOffline],
      ['gridFeedInPrioritizationFootnote', 'gridFeedInPrioritizationNote', gridFeedInPrioritizationVisible]
    ].map(([footnoteTarget, noteTarget, visible]) => this.setFootnoteVisibility(footnoteTarget, noteTarget, visible))
      .some(Boolean)

    if (gridFeedInPrioritizationVisible && this.hasGridFeedInPrioritizationNoteTextTarget) {
      this.gridFeedInPrioritizationNoteTextTarget.textContent = this.gridFeedInPrioritizationNotice
    }

    if (hasVisibleFootnotes) {
      this.element.classList.add("has-footnotes")
    } else {
      this.element.classList.remove("has-footnotes")
    }
  }

  setFootnoteVisibility(footnoteTarget, noteTarget, visible) {
    this.setTargetVisibility(footnoteTarget, visible)
    this.setTargetVisibility(noteTarget, visible)
    return !!visible && (this.hasTarget(footnoteTarget) || this.hasTarget(noteTarget))
  }

  setTargetVisibility(targetName, visible) {
    if (!this.hasTarget(targetName)) return

    this[`${targetName}Target`].classList[visible ? 'remove' : 'add']("hide")
  }

  hasTarget(targetName) {
    const targetProperty = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`
    return this[targetProperty]
  }

  hideAllConnections() {
    this.producerConnectionTarget?.classList.add('hide')
    this.consumerConnectionTarget?.classList.add('hide')
    this.gridConnectionTarget?.classList.add('hide')
    this.storageConnectionTarget?.classList.add('hide')
    this.heatingConnectionTarget?.classList.add('hide')
    this.chargingConnectionTarget?.classList.add('hide')
  }

  updateConnections(data) {
    if (this.gatewayOffline) {
      this.hideAllConnections()
      return
    }
    this.calculateConnectionBasis(data)

    if (this.hasGridCircleTarget && this.hasGridConnectionTarget) {
      this.updateGridConnection(data)
    }
    if (this.hasProducerCircleTarget && this.hasProducerConnectionTarget) {
      this.updateProducerConnection(data)
    }
    if (this.hasStorageCircleTarget && this.hasStorageConnectionTarget) {
      this.updateStorageConnection(data)
    }
    if (this.hasConsumerCircleTarget && this.hasConsumerConnectionTarget) {
      this.updateConsumerConnection(data)
    }
    this.updateSmallCircleConnections(data)
  }

  calculateConnectionBasis(data) {
    const emsValues = []
    const valueSources = [
      data.power_grid,
      data.producers_total_count > 0 ? data.power_production : null,
      data.power_storage,
      data.power_consumption_calc || data.power_consumption,
      data.power_charging_stations,
      data.power_heating,
      data.power_appliances
    ]
    valueSources.forEach(value => {
      if (value !== null && value !== undefined) {
        emsValues.push(Math.abs(value))
      }
    })
    this.connectionBasis = emsValues.length > 0 ? Math.max(...emsValues) : 1000
  }

  updateGridConnection(data) {
    if (this.hasOnlineDevices('grid') && data.power_grid != null) {
      this.processConnection({
        value: data.power_grid,
        category: "grid",
        circleTarget: this.gridCircleTarget,
        connectionTarget: this.gridConnectionTarget
      })
    } else {
      this.gridConnectionTarget?.classList.add('hide')
    }
  }

  updateProducerConnection(data) {
    if (this.hasOnlineDevices('producers') && data.power_production != null) {
      this.processConnection({
        value: data.power_production,
        category: "producer",
        circleTarget: this.producerCircleTarget,
        connectionTarget: this.producerConnectionTarget
      })
    } else {
      this.producerConnectionTarget?.classList.add('hide')
    }
  }

  updateStorageConnection(data) {
    if (this.hasOnlineDevices('storages') && data.power_storage != null) {
      this.processConnection({
        value: data.power_storage,
        category: "storage",
        circleTarget: this.storageCircleTarget,
        connectionTarget: this.storageConnectionTarget
      })
    } else {
      this.storageConnectionTarget?.classList.add('hide')
    }
  }

  updateConsumerConnection(data) {
    let energyConsumed = null
    if (data.power_consumption_calc != null) {
      energyConsumed = data.power_consumption_calc
    } else if (data.power_consumption != null) {
      energyConsumed = data.power_consumption
    }
    if (energyConsumed && energyConsumed > 0 && !this.specialCases.gridProducerBatteryAffected) {
      this.processConnection({
        value: energyConsumed,
        category: "consumer",
        circleTarget: this.consumerCircleTarget,
        connectionTarget: this.consumerConnectionTarget,
        usesDirectionalLogic: false
      })
    } else {
      this.consumerConnectionTarget?.classList.add('hide')
    }
  }

  processConnection(config) {
    try {
      let angle, distance
      const flowVal = helpers.truncateValue(config.value)

      if (config.category === "grid" || config.category === "storage") {
        if (flowVal < 0) {
          angle = this.getAngleBetweenCircles(this.centerCircleTarget, config.circleTarget)
          distance = this.getDistanceBetweenCircles(this.centerCircleTarget, config.circleTarget)
        } else {
          angle = this.getAngleBetweenCircles(config.circleTarget, this.centerCircleTarget)
          distance = this.getDistanceBetweenCircles(config.circleTarget, this.centerCircleTarget)
        }
      } else if (config.category === "consumer") {
        angle = this.getAngleBetweenCircles(this.centerCircleTarget, config.circleTarget)
        distance = this.getDistanceBetweenCircles(this.centerCircleTarget, config.circleTarget)
      } else {
        angle = this.getAngleBetweenCircles(config.circleTarget, this.centerCircleTarget)
        distance = this.getDistanceBetweenCircles(config.circleTarget, this.centerCircleTarget)
      }

      this.connectSingleCircle(
        flowVal,
        config.category,
        config.circleTarget,
        config.connectionTarget,
        angle,
        distance
      )
    } catch (error) {
      this.debug && console.warn(`[EnergyFlowGraph] Failed to process ${config.category} connection:`, error)
      config.connectionTarget?.classList.add('hide')
    }
  }

  updateSmallCircleConnections(data) {
    if (this.hasHeatingCircleTarget && this.hasHeatingConnectionTarget) {
      this.updateHeatingConnection(data)
    }
    if (this.hasChargingCircleTarget && this.hasChargingConnectionTarget) {
      this.updateChargingConnection(data)
    }

    const chargingPointOnline = this.hasOnlineDevices('charging')
    const heatingOnline = this.hasOnlineDevices('heating')
    this.processGeneralConsumptionConnection(data, chargingPointOnline, heatingOnline)
  }

  updateHeatingConnection(data) {
    if (this.hasOnlineDevices('heating') && data.power_heating != null && data.power_heating > 0) {
      this.processSmallCircleConnection({
        value: data.power_heating,
        category: "heating",
        circleTarget: this.heatingCircleTarget,
        connectionTarget: this.heatingConnectionTarget,
      })
    } else {
      this.heatingConnectionTarget?.classList.add('hide')
    }
  }

  updateChargingConnection(data) {
    if (this.hasOnlineDevices('charging') && data.power_charging_stations != null && data.power_charging_stations > 0) {
      this.processSmallCircleConnection({
        value: data.power_charging_stations,
        category: "charging",
        circleTarget: this.chargingCircleTarget,
        connectionTarget: this.chargingConnectionTarget,
      })
    } else {
      this.chargingConnectionTarget?.classList.add('hide')
    }
  }

  processSmallCircleConnection(config) {
    try {
      const { angle, distance } = this.calculateSmallCircleConnectionGeometry(config.circleTarget)

      this.connectSingleCircle(
        helpers.truncateValue(config.value),
        config.category,
        config.circleTarget,
        config.connectionTarget,
        angle,
        distance
      )
    } catch (error) {
      this.debug && console.warn(`[EnergyFlowGraph] Failed to process ${config.category} connection:`, error)
      config.connectionTarget?.classList.add('hide')
    }
  }

  calculateSmallCircleConnectionGeometry(circleTarget) {
    const circlePos = this.getCirclePos(circleTarget)
    const consumerPos = this.getCirclePos(this.consumerCircleTarget)
    const distance = Math.sqrt(
      Math.pow(circlePos.x - consumerPos.x, 2) + Math.pow(circlePos.y - consumerPos.y, 2)
    )
    const angle = Math.atan2(circlePos.y - consumerPos.y, circlePos.x - consumerPos.x) * 180 / Math.PI

    return { angle, distance }
  }

  processGeneralConsumptionConnection(data, chargingPointOnline, heatingOnline) {
    // Connect general consumption - this flows FROM main consumer TO general consumption
    // Only show this flow when some heating/charging devices are online
    // If no heating/charging devices are online, there should be no flow to general consumption
    let shouldShowGeneralConsumption = false
    let generalConsumptionPower = 0

    if ((chargingPointOnline || heatingOnline) && data.power_appliances !== null && data.power_appliances !== undefined && data.power_appliances > 0) {
      shouldShowGeneralConsumption = true
      generalConsumptionPower = data.power_appliances
    }
    // Note: When no heating/charging is online, we don't show general consumption flow
    // The full consumption goes directly from autarky to main consumer circle

    if (shouldShowGeneralConsumption && generalConsumptionPower > 0 &&
        !this.specialCases.gridProducerBatteryAffected &&
        !this.specialCases.consumersAffected) {
      if (this.hasGeneralConsumptionCircleTarget && this.hasGeneralConsumptionConnectionTarget) {
        try {
          const { angle, distance } = this.calculateSmallCircleConnectionGeometry(this.generalConsumptionCircleTarget)

          this.connectSingleCircle(
            helpers.truncateValue(generalConsumptionPower),
            "generalConsumption",
            this.generalConsumptionCircleTarget,
            this.generalConsumptionConnectionTarget,
            angle,
            distance
          )
        } catch (error) {
          this.debug && console.warn('[EnergyFlowGraph] Failed to process general consumption connection:', error)
          this.generalConsumptionConnectionTarget?.classList.add('hide')
        }
      }
    } else {
      this.generalConsumptionConnectionTarget?.classList.add('hide')
    }
  }

  clearAnimations() {
    const arrows = document.getElementsByClassName("animation-dataflow")
    if (this.debug && arrows.length > 0) {
      console.log('[EnergyFlowGraph] Clearing animations', {
        count: arrows.length,
        stack: new Error().stack
      })
    }
    while (arrows.length > 0) {
      arrows[0].remove()
    }

    const styleSheets = document.getElementsByClassName("animation-stylesheet")
    while (styleSheets.length > 0) {
      styleSheets[0].remove()
    }
  }

  connectSingleCircle(flowVal, category, circle, connectionRect, angle, distance) {
    if (flowVal === null || flowVal === undefined) {
      connectionRect?.classList.add("hide")
      return
    }

    let thickness = this.getThickness(flowVal)

    if (connectionRect) {
      let connectionStartPoint
      if (category === "heating" || category === "charging" || category === "generalConsumption") {
        connectionStartPoint = this.getCirclePos(this.consumerCircleTarget)
      } else if (category === "consumer") {
        connectionStartPoint = this.getCirclePos(this.centerCircleTarget)
      } else if (category === "grid" || category === "storage") {
        if (flowVal < 0) {
          connectionStartPoint = this.getCirclePos(this.centerCircleTarget)
        } else {
          connectionStartPoint = this.getCirclePos(circle)
        }
      } else {
        connectionStartPoint = this.getCirclePos(circle)
      }

      const rectX = connectionStartPoint.x
      const rectY = connectionStartPoint.y

      if (isNaN(rectX) || isNaN(rectY) || isNaN(distance) || isNaN(thickness) || isNaN(angle)) {
        if (this.debug) {
          console.warn('[EnergyFlowGraph] Invalid coordinate values for connection rectangle:', {
            rectX, rectY, distance, thickness, angle, category
          })
        }
        connectionRect?.classList.add('hide')
        return
      }

      connectionRect.setAttributeNS(null, "x", rectX)
      connectionRect.setAttributeNS(null, "y", rectY - thickness / 2)
      connectionRect.setAttributeNS(null, "width", distance)
      connectionRect.setAttributeNS(null, "height", thickness)
      connectionRect.setAttributeNS(null, "transform", `rotate(${angle}, ${rectX}, ${rectY})`)
      connectionRect.setAttributeNS(null, "fill", "none")
      connectionRect.setAttributeNS(null, "stroke", "#bcbcbc")
      connectionRect.setAttributeNS(null, "stroke-width", "1")
      connectionRect.classList.remove("hide")
    }

    if (flowVal && flowVal !== 0 && flowVal !== '0' && parseFloat(flowVal) !== 0) {
      let sourceCircle, targetCircle

      if (category === "heating" || category === "charging" || category === "generalConsumption") {
        sourceCircle = this.consumerCircleTarget
        targetCircle = circle
      } else if (category === "consumer") {
        sourceCircle = this.centerCircleTarget
        targetCircle = circle
      } else if (category === "grid" || category === "storage") {
        if (flowVal < 0) {
          sourceCircle = this.centerCircleTarget
          targetCircle = circle
        } else {
          sourceCircle = circle
          targetCircle = this.centerCircleTarget
        }
      } else {
        sourceCircle = circle
        targetCircle = this.centerCircleTarget
      }

      if (sourceCircle && targetCircle) {
        this.animate(sourceCircle, targetCircle, thickness, flowVal, category)
      } else {
        console.error(`Missing circles for ${category}:`, { sourceCircle, targetCircle })
      }
    }
  }

  animate(circle1, circle2, thicknessRect, emsValue, category) {
    let thickness = thicknessRect - 2
    let color = COLOR_ARROW_CONSUMER

    if (category == 'producer') {
      color = COLOR_ARROW_PRODUCER
    } else if (category === 'grid' || category === 'storage') {
      if (emsValue < 0) {
        color = COLOR_ARROW_CONSUMER
      } else {
        color = COLOR_ARROW_PRODUCER
      }
    }

    let speedMin = 20
    let speedMax = 12
    let speedBasis = speedMin - speedMax
    let minArrows = 3
    let maxArrows = Math.round(650 * 0.009)
    let arrowBasis = maxArrows - minArrows
    let arrowThickness = 15
    let arrowHead = (thickness / arrowThickness) * 5

    let percentOfTotalPower = (100.0 / (this.connectionBasis === 0.0 ? 100.0 : this.connectionBasis)) * Math.abs(emsValue)
    let speed = speedMin - (speedBasis / 100.0) * percentOfTotalPower
    let amountArrows = minArrows + Math.round((arrowBasis / 100.0) * percentOfTotalPower)

    const linePos = this.getLinePos(circle1, circle2)
    let xStart = linePos.x1
    let yStart = linePos.y1
    let xEnd = linePos.x2
    let yEnd = linePos.y2

    let angle = Math.atan2(yEnd - yStart, xEnd - xStart) * 180 / Math.PI

    const uniqueId = Date.now() + Math.random().toString(36).substr(2, 9)
    const animationName = `${category}-animation-${uniqueId}`

    let arrowStyleSheet = document.createElement("style")
    arrowStyleSheet.type = "text/css"
    arrowStyleSheet.classList.add("animation-stylesheet")
    arrowStyleSheet.innerHTML =
      `@keyframes ${animationName} {
        0%   { transform: translate(${xStart}px, ${yStart}px) rotate(${angle}deg); }
        100% { transform: translate(${xEnd}px, ${yEnd}px) rotate(${angle}deg); }
      }`
    document.head.appendChild(arrowStyleSheet)

    let start = speed * -1
    let speedIncrease = speed / amountArrows

    for (let i = 0; i < amountArrows; i++) {
      const arrow = document.createElementNS("http://www.w3.org/2000/svg", "path")
      arrow.setAttributeNS(null, "class", "animation-dataflow")
      arrow.setAttributeNS(null, "d",
        `M${thickness / -2} ${thickness / -2} H${arrowThickness} L${arrowHead + arrowThickness} 0 L${arrowThickness} ${thickness / 2} H0 L${arrowHead} 0 L0 ${thickness / -2}`)
      arrow.setAttributeNS(null, "fill", color)
      arrow.setAttributeNS(null, "opacity", "0.6")
      arrow.style.animation = `${speed}s linear ${start}s infinite normal none running ${animationName}`

      const connections = this.element.querySelector('#connections')
      if (connections) connections.appendChild(arrow)

      start += speedIncrease
    }
  }

  getThickness(value) {
    const maxConnectionThickness = 650 * 0.085
    const minConnectionThickness = 650 * 0.0085
    let calcBasis = maxConnectionThickness - minConnectionThickness

    if (value == 0 || this.connectionBasis === 0) {
      return 0.5
    } else {
      return (calcBasis / this.connectionBasis) * Math.abs(value) + minConnectionThickness
    }
  }

  getCirclePos(el) {
    let circleX = 0
    let circleY = 0
    if (el) {
      const cxAttr = el.getAttributeNode("cx")
      const cyAttr = el.getAttributeNode("cy")
      if (cxAttr && cxAttr.value !== null && cxAttr.value !== undefined) {
        const parsedX = parseInt(cxAttr.value)
        circleX = isNaN(parsedX) ? 0 : parsedX
      }
      if (cyAttr && cyAttr.value !== null && cyAttr.value !== undefined) {
        const parsedY = parseInt(cyAttr.value)
        circleY = isNaN(parsedY) ? 0 : parsedY
      }

      const parentGroup = el.parentElement
      if (parentGroup && parentGroup.hasAttribute('transform')) {
        const transform = parentGroup.getAttribute('transform')

        const translateIndex = transform.indexOf('translate(')
        if (translateIndex !== -1) {
          const start = translateIndex + 10
          const end = transform.indexOf(')', start)
          if (end !== -1 && end - start < 50) {
            const coords = transform.substring(start, end).split(',')
            if (coords.length === 2) {
              const groupX = parseFloat(coords[0].trim())
              const groupY = parseFloat(coords[1].trim())
              if (!isNaN(groupX)) circleX += groupX
              if (!isNaN(groupY)) circleY += groupY
            }
          }
        }
      }
    }
    return { x: circleX, y: circleY }
  }

  getLinePos(circle1, circle2) {
    const c1 = this.getCirclePos(circle1)
    const c2 = this.getCirclePos(circle2)
    return { x1: c1.x, y1: c1.y, x2: c2.x, y2: c2.y }
  }

  getAngleBetweenCircles(circle1, circle2) {
    if (!circle1 || !circle2) {
      console.warn('[EnergyFlowGraph] Missing circle elements for angle calculation')
      return 0
    }

    const pos1 = this.getCirclePos(circle1)
    const pos2 = this.getCirclePos(circle2)

    if (isNaN(pos1.x) || isNaN(pos1.y) || isNaN(pos2.x) || isNaN(pos2.y)) {
      console.warn('[EnergyFlowGraph] Invalid positions for angle calculation:', { pos1, pos2 })
      return 0
    }

    const angle = Math.atan2(pos2.y - pos1.y, pos2.x - pos1.x) * 180 / Math.PI
    return isNaN(angle) ? 0 : angle
  }

  getDistanceBetweenCircles(circle1, circle2) {
    if (!circle1 || !circle2) {
      console.warn('[EnergyFlowGraph] Missing circle elements for distance calculation')
      return 0
    }

    const pos1 = this.getCirclePos(circle1)
    const pos2 = this.getCirclePos(circle2)

    if (isNaN(pos1.x) || isNaN(pos1.y) || isNaN(pos2.x) || isNaN(pos2.y)) {
      console.warn('[EnergyFlowGraph] Invalid positions for distance calculation:', { pos1, pos2 })
      return 0
    }

    const distance = Math.sqrt(Math.pow(pos2.x - pos1.x, 2) + Math.pow(pos2.y - pos1.y, 2))
    return isNaN(distance) ? 0 : distance
  }

  uiOptimizedValue(value, unit = 'W') {
    if (value == null || isNaN(value)) {
      return null
    }
    return this.formatValue(value, unit)
  }

  formatValue(value, unit) {
    const fromUnit = unit === 'Wh' ? 'W' : unit
    const toUnit = fromUnit === 'W' ? 'kW' : `k${fromUnit}`

    const converted = helpers.convertToUnit(value).from(fromUnit).to(toUnit)

    if (converted >= 1000) {
      const megaValue = converted / 1000
      const truncated = helpers.truncateValue(megaValue, 1000)
      const megaUnit = unit === 'Wh' ? 'MWh' : `M${unit.substring(0, 1)}`
      return `${truncated} ${megaUnit}`
    } else {
      const truncated = helpers.truncateValue(converted, 1000)
      const kiloUnit = unit === 'Wh' ? 'kWh' : toUnit
      return `${truncated} ${kiloUnit}`
    }
  }

  setCircleValue(valueKey, targetKey, value) {
    this.flowValues[valueKey] = value
    const target = this[targetKey]
    if (target) {
      target.textContent = value
    }
  }

  showCircle(targetKey) {
    const target = this[targetKey]
    if (target) {
      target.classList.remove("hide")
    }
  }

  processCircleValue(config, data) {
    // Check if the category has any devices (offlineStates exists)
    if (config.offlineStateKey && !this.offlineStates[config.offlineStateKey]) {
      return
    }

    if (config.circleTargetKey) {
      this.showCircle(config.circleTargetKey)
    }

    if (this.gatewayOffline) {
      this.setCircleValue(config.valueKey, config.targetKey, "-")
    } else if (config.offlineStateKey && this.offlineStates[config.offlineStateKey]?.allOffline) {
      this.setCircleValue(config.valueKey, config.targetKey, "-")
    } else {
      const value = data[config.dataKey]

      if (value !== null && value !== undefined) {
        const optimizedValue = this.uiOptimizedValue(value)
        this.setCircleValue(config.valueKey, config.targetKey, optimizedValue)
      } else {
        this.setCircleValue(config.valueKey, config.targetKey, "-")
      }
    }

  }
}
